import { useEffect, useState, useCallback } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { PageHeader, Card, money, StatusBadge, DateRangeFilter, rangeParams } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, Pencil, Trash2, Network as NetworkIcon } from "lucide-react";

const TEMPLATES = ["clickbank", "maxbounty", "cj", "shareasale", "rakuten", "impact", "performcb", "custom"];

const NetworkForm = ({ initial, onSave }) => {
  const [form, setForm] = useState(
    initial || { name: "", template: "custom", website: "", api_key: "", tracking_domain: "", postback_param_clickid: "click_id", postback_param_payout: "payout", status: "active", notes: "" }
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Network Name</Label>
          <Input data-testid="network-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Template</Label>
          <Select value={form.template} onValueChange={(v) => set("template", v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{TEMPLATES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Website</Label>
          <Input value={form.website} onChange={(e) => set("website", e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">API Key (optional)</Label>
          <Input value={form.api_key} onChange={(e) => set("api_key", e.target.value)} className="mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Cloaked Tracking Domain (optional — e.g. goaheadwith.com)</Label>
        <Input data-testid="network-tracking-domain-input" value={form.tracking_domain || ""} onChange={(e) => set("tracking_domain", e.target.value.trim())} className="mt-1" placeholder="All offers of this network use this domain for tracking links" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Network's Click ID param</Label>
          <Input value={form.postback_param_clickid} onChange={(e) => set("postback_param_clickid", e.target.value)} className="mt-1 font-mono text-xs" />
        </div>
        <div>
          <Label className="text-xs">Network's Payout param</Label>
          <Input value={form.postback_param_payout} onChange={(e) => set("postback_param_payout", e.target.value)} className="mt-1 font-mono text-xs" />
        </div>
      </div>
      <Button data-testid="network-save-button" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!form.name} onClick={() => onSave(form)}>
        {initial ? "Update Network" : "Add Network"}
      </Button>
    </div>
  );
};

export default function Networks() {
  const [networks, setNetworks] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rangeSel, setRangeSel] = useState({ range: "this_month" });

  const load = useCallback(() => {
    Promise.all([api.get("/networks"), api.get(`/stats/entities?${rangeParams(rangeSel)}`)]).then(([n, st]) => { setNetworks(n.data); setStats(st.data); });
  }, [rangeSel]);
  useEffect(load, [load]);

  const save = async (form) => {
    if (editing) await api.put(`/networks/${editing.id}`, form);
    else await api.post("/networks", form);
    toast.success(editing ? "Network updated" : "Network added");
    setOpen(false); setEditing(null); load();
  };
  const del = async (id) => { await api.delete(`/networks/${id}`); toast.success("Network deleted"); load(); };

  const postbackUrl = (n) =>
    `${BACKEND_URL}/api/postback?click_id={${n.postback_param_clickid || "click_id"}}&payout={${n.postback_param_payout || "payout"}}&tx_id={transaction_id}&status=approved&net=${n.id}`;

  const copyPostback = (n) => {
    navigator.clipboard.writeText(postbackUrl(n));
    toast.success("Postback URL copied — paste it into the network's S2S settings");
  };

  return (
    <div data-testid="networks-page">
      <PageHeader
        title="Affiliate Networks"
        subtitle="S2S postback integration per network"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-network-button" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1" /> Add Network</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Network" : "Add Affiliate Network"}</DialogTitle></DialogHeader>
              <NetworkForm key={editing?.id || "new"} initial={editing} onSave={save} />
            </DialogContent>
          </Dialog>
        }
      />
      <div className="mb-4 fade-up"><DateRangeFilter value={rangeSel} onChange={setRangeSel} /></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 fade-up" data-testid="networks-grid">
        {networks.map((n) => {
          const st = stats[n.id] || { clicks: 0, conversions: 0, revenue: 0 };
          return (
            <Card key={n.id} className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    <NetworkIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-slate-900">{n.name}</div>
                    <div className="text-xs text-slate-400">{n.website || n.template}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={n.status} />
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(n); setOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => del(n.id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <div className="font-mono text-sm font-semibold text-slate-900">{st.clicks.toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Clicks</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <div className="font-mono text-sm font-semibold text-slate-900">{st.conversions}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Conversions</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
                  <div className="font-mono text-sm font-semibold text-emerald-700">{money(st.revenue)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-600">Revenue</div>
                </div>
              </div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">S2S Postback URL — add to {n.name} settings</Label>
              <div className="mt-1.5 bg-slate-900 rounded-lg p-2.5 flex items-center gap-2">
                <code className="text-[10px] text-emerald-400 break-all flex-1">{postbackUrl(n)}</code>
                <button data-testid={`copy-postback-${n.id}`} onClick={() => copyPostback(n)} className="text-slate-400 hover:text-white shrink-0"><Copy className="h-3.5 w-3.5" /></button>
              </div>
            </Card>
          );
        })}
      </div>
      {networks.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No networks yet.</div>}
    </div>
  );
}
